/**
 * Awtomic Cart Rollup v1.0.0 - Browser Console Version
 *
 * ⚠️  CONSOLE TEST VERSION - Extra safety checks for Rebuy
 *
 * This version includes defensive checks to avoid breaking Rebuy cart.
 *
 * Copy and paste this entire file into your browser console to test
 */

// Enable debug mode for console testing
window.awtomicCartExtrasConfig = window.awtomicCartExtrasConfig || {};
window.awtomicCartExtrasConfig.debug = true;

(function () {
  'use strict';

  // Allow custom theme configuration via window.awtomicCartExtrasConfig
  const customConfig = window.awtomicCartExtrasConfig || {};
  const DEBUG_MODE = customConfig.debug || false;

  // Debug logging helper
  const debug = (...args) => {
    if (DEBUG_MODE) {
      console.log('[Awtomic Cart Rollup]', ...args);
    }
  };

  // Common selectors for Horizon/Tinker (share same cart structure)
  const COMMON_SELECTORS = {
    cartItemRow: 'tr[data-key]',
    title: '.cart-items__title',
    image: '.cart-items__media-image',
    imageContainer: '.cart-items__media-container',
    imageLink: '.cart-items__link',
    variants: '.cart-items__variants',
    variantsWrapper: '.cart-items__variants-wrapper',
    unitPriceWrapper: '.cart-items__unit-price-wrapper',
    price: '.cart-items__price',
    compareAtPrice: '.compare-at-price',
    qtyInput: 'input[type="number"]',
    qtyButtons: '.quantity-minus, .quantity-plus',
    removeButton: '.cart-items__remove',
  };

  // Theme detection function - can be called multiple times
  const detectTheme = () => {
    if (customConfig.theme && typeof customConfig.theme === 'object') {
      debug(
        'Using custom theme configuration:',
        customConfig.theme.name || 'Custom',
      );
      return {
        name: customConfig.theme.name || 'Custom',
        detect: customConfig.theme.detect || (() => true),
        selectors: customConfig.theme.selectors || {},
        drawer:
          customConfig.theme.drawer !== undefined
            ? customConfig.theme.drawer
            : null,
        cartBubble: customConfig.theme.cartBubble || {},
      };
    }

    const themes = {
      horizon: {
        name: 'Horizon',
        detect: () =>
          document.querySelector('tr[data-key]') &&
          document.querySelector('.cart-items__title'),
        selectors: COMMON_SELECTORS,
        drawer: {
          selectors: 'cart-drawer, [data-drawer], .cart-drawer, #cart-drawer',
          visibilityCheck: (el) =>
            el?.offsetParent ||
            el?.hasAttribute('open') ||
            el?.classList.contains('active') ||
            el?.classList.contains('is-open'),
          itemsComponent: 'cart-items-component[data-drawer]',
          closestContainer: 'cart-drawer, .cart-drawer',
        },
        cartBubble: {
          count: '.cart-bubble__text-count',
          visuallyHidden: '.cart-bubble__text .visually-hidden',
          container: '.cart-bubble',
        },
      },
      tinker: {
        name: 'Tinker',
        detect: () =>
          document.querySelector('dialog.cart-drawer__dialog') &&
          document.querySelector('cart-items-component[data-drawer]'),
        selectors: COMMON_SELECTORS,
        drawer: {
          selectors: 'dialog.cart-drawer__dialog[open], dialog[open]',
          visibilityCheck: (el) => el?.hasAttribute('open'),
          itemsComponent: 'cart-items-component[data-drawer]',
          closestContainer: 'dialog.cart-drawer__dialog',
        },
        cartBubble: {
          count: '.cart-bubble__text-count',
          visuallyHidden: '.cart-bubble__text .visually-hidden',
          container: '.cart-bubble',
        },
      },
      dawn: {
        name: 'Dawn',
        detect: () => {
          const hasQuantityPopover =
            !!document.querySelector('quantity-popover');
          const hasCartRemoveButton =
            !!document.querySelector('cart-remove-button');
          const hasCartItems = !!document.querySelector('cart-items');
          const hasCartItemClass = !!document.querySelector('.cart-item__name');
          const hasCartItemImage = !!document.querySelector(
            '.cart-item__image-container',
          );
          const isCartPage = window.location.pathname.includes('/cart');

          const detected =
            hasQuantityPopover ||
            hasCartRemoveButton ||
            hasCartItems ||
            (isCartPage && (hasCartItemClass || hasCartItemImage));

          if (detected) {
            debug('Dawn theme detection details:', {
              hasQuantityPopover,
              hasCartRemoveButton,
              hasCartItems,
              hasCartItemClass,
              hasCartItemImage,
              isCartPage,
            });
          }

          return detected;
        },
        selectors: {
          cartItemRow: 'tr.cart-item, .cart-item',
          title: '.cart-item__name, a.cart-item__name',
          image: '.cart-item__image',
          imageContainer: '.cart-item__image-container',
          imageLink: 'a.cart-item__link',
          variants: '.product-option',
          variantsWrapper: '.cart-item__details',
          unitPriceWrapper: '.unit-price, .cart-item__totals',
          price: '.price--end, .product-option',
          compareAtPrice: '.price--end',
          qtyInput: 'input.quantity__input',
          qtyButtons: '.quantity__button',
          removeButton: 'cart-remove-button a, .cart-remove-button a',
        },
        drawer: null,
        cartBubble: {
          count: '.cart-count-bubble span[aria-hidden="true"]',
          visuallyHidden: '.cart-count-bubble .visually-hidden',
          container: '.cart-count-bubble',
        },
      },
      supply: {
        name: 'Supply',
        detect: () =>
          (!!document.querySelector('#CartDrawer.drawer, .drawer#CartDrawer') ||
            !!document.querySelector('[data-products]') ||
            window.location.pathname.includes('/cart')) &&
          !!document.querySelector('.cart__items, .cart__item'),
        selectors: {
          cartItemRow: '.cart__item[data-key]',
          title: '.cart__item-name',
          image: '.cart__image img',
          imageContainer: '.cart__image',
          imageLink: '.cart__image a',
          variants: '.cart__item--variants',
          variantsWrapper: '.cart__item-details',
          unitPriceWrapper: '.cart__item-price-col',
          price: '.cart__price',
          compareAtPrice: '.cart__price--strikethrough',
          qtyInput: 'input.js-qty__num',
          qtyButtons: '.js-qty__adjust',
          removeButton: '.cart__remove a',
        },
        drawer: {
          selectors: '#CartDrawer.drawer--is-open, .drawer.drawer--is-open',
          visibilityCheck: (el) => el?.classList.contains('drawer--is-open'),
          itemsComponent: '.cart__items',
          closestContainer: '#CartDrawer, .drawer',
        },
        cartBubble: {
          count:
            '[data-testid="cart-bubble"], .cart-count, [id*="cart-count"], .cart-count-bubble span[aria-hidden="true"], .cart-bubble__text-count',
          visuallyHidden: '.visually-hidden',
          container: '.cart-bubble, .cart-count-bubble',
        },
      },
      rebuy: {
        name: 'Rebuy',
        detect: () => !!document.querySelector('#rebuy-cart, .rebuy-cart'),
        selectors: {
          cartItemRow: 'li.rebuy-cart__flyout-item',
          title: '.rebuy-cart__flyout-item-product-title',
          image: '.rebuy-cart__flyout-item-media img',
          imageContainer: '.rebuy-cart__flyout-item-media',
          imageLink: '.rebuy-cart__flyout-item-media a',
          variants: '.rebuy-cart__flyout-item-discount-message',
          variantsWrapper: '.rebuy-cart__flyout-item-info',
          unitPriceWrapper: '.rebuy-cart__flyout-item-price',
          price:
            '.rebuy-money.sale span[tabindex], .rebuy-money span[tabindex]',
          compareAtPrice: '.rebuy-money.compare-at span',
          qtyInput: '.rebuy-cart__flyout-item-quantity-widget-label',
          qtyButtons: '.rebuy-cart__flyout-item-quantity-widget-button',
          removeButton: '.rebuy-cart__flyout-item-remove',
        },
        drawer: {
          selectors:
            '#rebuy-cart.is-visible, #rebuy-cart[aria-hidden="false"], .rebuy-cart.is-visible',
          visibilityCheck: (el) =>
            el?.classList.contains('is-visible') ||
            el?.getAttribute('aria-hidden') === 'false',
          itemsComponent: 'ul[data-smartcart-items]',
          closestContainer: '#rebuy-cart, .rebuy-cart',
        },
        cartBubble: {
          count:
            '[data-testid="cart-bubble"], .cart-count, [id*="cart-count"], .cart-count-bubble span[aria-hidden="true"], .cart-bubble__text-count',
          visuallyHidden: '.visually-hidden',
          container: '.cart-bubble, .cart-count-bubble',
        },
      },
      generic: {
        name: 'Generic',
        detect: () => true,
        selectors: {
          cartItemRow: '[data-key]',
          title: 'a[href*="/products/"]',
          image: 'img[src*="cdn.shopify.com"]',
          imageContainer: '.image-container, .media-container',
          imageLink: 'a[href*="/products/"]',
          variants: 'dl, .variant',
          variantsWrapper: '.variants-wrapper, .product-options',
          unitPriceWrapper: '.price-wrapper, .unit-price',
          price: '.price, .money',
          compareAtPrice: '.compare-at-price, .was-price',
          qtyInput: 'input[type="number"]',
          qtyButtons: 'button[name*="plus"], button[name*="minus"]',
          removeButton: 'button[class*="remove"], a[class*="remove"]',
        },
        drawer: {
          selectors:
            'cart-drawer, [data-drawer], .cart-drawer, #cart-drawer, dialog[open]',
          visibilityCheck: (el) =>
            el?.offsetParent ||
            el?.hasAttribute('open') ||
            el?.classList.contains('active') ||
            el?.classList.contains('is-open'),
          itemsComponent: 'cart-items-component[data-drawer]',
          closestContainer: 'cart-drawer, .cart-drawer, dialog',
        },
        cartBubble: {
          count:
            '[data-testid="cart-bubble"], .cart-count, [id*="cart-count"], .cart-count-bubble span[aria-hidden="true"], .cart-bubble__text-count',
          visuallyHidden: '.visually-hidden',
          container: '.cart-bubble, .cart-count-bubble',
        },
      },
    };

    // Check themes in priority order (more specific first)
    // Note: Rebuy is NOT checked here - it's always checked separately in getCartContext
    const themeOrder = ['tinker', 'horizon', 'dawn', 'supply', 'generic'];
    for (const themeName of themeOrder) {
      const theme = themes[themeName];
      if (theme && theme.detect()) {
        debug(`Detected theme: ${theme.name}`);
        if (themeName !== 'generic') {
          debug('Theme selectors:', theme.selectors);
          debug('Theme drawer config:', theme.drawer);
        }
        return theme;
      }
    }
    debug('⚠️  No specific theme detected, using generic fallback');
    return themes.generic;
  };

  // Initial theme detection
  let THEME = detectTheme();

  // Initialization message
  debug('='.repeat(60));
  debug('🧪 CONSOLE TEST VERSION - Enhanced Safety');
  debug('Debug Mode Enabled');
  debug('Version: 1.0.0 (Console Test - Rebuy Safe)');
  debug('Theme:', THEME.name);
  debug('Custom Config:', customConfig.theme ? 'Yes' : 'No');
  debug('Has Drawer:', THEME.drawer ? 'Yes' : 'No');
  debug('⚠️  Extra safety checks enabled for Rebuy compatibility');
  debug('');
  debug('📌 TESTING TIPS:');
  debug('  • For cart page: Make sure you are on /cart');
  debug('  • For drawer: Open the cart drawer BEFORE running this script');
  debug('  • Run window.awtomicCartRollup.analyze() to re-test after changes');
  debug('='.repeat(60));

  // Skip anti-flicker CSS for console version
  const injectAntiFlickerCSS = () => {
    debug('Skipping anti-flicker CSS for console version');
  };

  const removeAntiFlickerCSS = () => {
    debug('Skipping anti-flicker CSS removal for console version');
  };

  let isProcessing = false;
  let lastProcessedTime = 0;
  let observers = { drawer: null, rebuy: null };
  const productCache = new Map();
  const lastCartData = { page: null, drawer: null, rebuy: null };
  const rollupAlreadyDone = { page: false, drawer: false, rebuy: false };
  let pollInterval = null;
  let lastVisibleCarts = { rebuy: false, drawer: false };

  const fetchCartData = async () => {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      debug('Error fetching cart data:', err);
      return null;
    }
  };

  const fetchBundleProductData = async (bundleHandle, variantId) => {
    const cacheKey = `${bundleHandle}:${variantId}`;
    if (productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    try {
      const response = await fetch(`/products/${bundleHandle}.js`);
      if (!response.ok) {
        throw new Error(`Product fetch failed: ${response.status}`);
      }

      const productData = await response.json();
      const result = {
        product: productData,
        variant: productData.variants.find(
          (v) => v.id.toString() === variantId.split('/').pop(),
        ),
      };
      productCache.set(cacheKey, result);
      return result;
    } catch (err) {
      debug('Error fetching bundle product data:', err);
      return null;
    }
  };

  const groupBundleItems = (cartData) => {
    if (!cartData?.items) {
      return {};
    }
    return cartData.items.reduce((groups, item) => {
      const bundleId = item.properties?.['_bundleUid'];
      if (bundleId) {
        if (!groups[bundleId]) {
          groups[bundleId] = { items: [], properties: item.properties };
        }
        groups[bundleId].items.push(item);
      }
      return groups;
    }, {});
  };

  const getCartContext = () => {
    debug('getCartContext() called');
    const contexts = [];

    // Check for Rebuy cart (can coexist with any theme) - ALWAYS checked
    // CONSOLE VERSION: With extra safety checks
    const rebuyCart = document.querySelector('#rebuy-cart, .rebuy-cart');
    if (
      rebuyCart &&
      (rebuyCart.classList.contains('is-visible') ||
        rebuyCart.getAttribute('aria-hidden') === 'false')
    ) {
      // Safety: Only process if Rebuy cart has bundle items
      const rebuyItems = rebuyCart.querySelectorAll(
        'li.rebuy-cart__flyout-item',
      );
      const hasBundleItems = Array.from(rebuyItems).some((item) => {
        const classList = Array.from(item.classList);
        return classList.some((c) => c.startsWith('property-_bundleuid:'));
      });

      if (hasBundleItems) {
        contexts.push({ type: 'rebuy', container: rebuyCart, theme: 'rebuy' });
        debug('Found Rebuy cart with bundle items');
      } else {
        debug(
          '⚠️  Rebuy cart found but no bundle items detected - skipping to avoid conflicts',
        );
      }
    }

    // Check for drawer cart using theme-specific selectors (skip if theme doesn't support drawer)
    const drawerConfig = THEME.drawer;
    debug('Checking for drawer, drawerConfig:', drawerConfig);
    if (drawerConfig && THEME.name !== 'Rebuy') {
      const drawer = document.querySelector(drawerConfig.selectors);
      debug('Drawer element:', drawer);
      debug(
        'Drawer visibility check:',
        drawer ? drawerConfig.visibilityCheck(drawer) : 'no drawer',
      );

      // CONSOLE VERSION: Extra debug for Supply drawer
      if (THEME.name === 'Supply') {
        const allDrawers = document.querySelectorAll('#CartDrawer, .drawer');
        debug(`Supply: Found ${allDrawers.length} drawer element(s)`);
        allDrawers.forEach((d, i) => {
          debug(`  Drawer ${i + 1}:`, {
            id: d.id,
            classes: Array.from(d.classList),
            hasOpenClass: d.classList.contains('drawer--is-open'),
            visible: d.offsetParent !== null,
          });
        });
      }

      if (drawer && drawerConfig.visibilityCheck(drawer)) {
        contexts.push({ type: 'drawer', container: drawer, theme: THEME.name });
        debug(`Found ${THEME.name} drawer cart`);
      } else {
        const drawerCartItems = document.querySelector(
          drawerConfig.itemsComponent,
        );
        debug('Drawer cart items:', drawerCartItems);
        debug('Drawer cart items offsetParent:', drawerCartItems?.offsetParent);
        if (drawerCartItems?.offsetParent) {
          const closestDrawer = drawerCartItems.closest(
            drawerConfig.closestContainer,
          );
          contexts.push({
            type: 'drawer',
            container: closestDrawer || drawerCartItems,
            theme: THEME.name,
          });
          debug(`Found ${THEME.name} drawer cart (via items component)`);
        } else {
          debug(`⚠️  ${THEME.name} drawer not detected or not open`);
        }
      }
    }

    // Check for cart page
    debug('Checking for cart page, pathname:', window.location.pathname);
    const isCartPage =
      window.location.pathname.includes('/cart') ||
      document.querySelector(
        '.cart-page, [data-cart-page], main.cart, cart-items',
      );
    debug('isCartPage:', isCartPage);
    debug('THEME.name:', THEME.name);
    if (isCartPage && THEME.name !== 'Rebuy') {
      contexts.push({ type: 'page', container: document, theme: THEME.name });
      debug('Found cart page');
    }

    debug('Final contexts:', contexts);
    return contexts.length > 0 ? contexts : null;
  };

  const findAndMatchCartItems = (cartData, tbody, selectors, themeName) => {
    const effectiveSelectors = selectors || THEME.selectors;
    const effectiveTheme = themeName || THEME.name;
    const matchedCartItemIndices = new Set();

    return Array.from(tbody.querySelectorAll(effectiveSelectors.cartItemRow))
      .map((row) => {
        let cartItem;
        let cartItemIndex = -1;

        if (row.dataset.key) {
          cartItemIndex = cartData.items.findIndex(
            (item) => item.key === row.dataset.key,
          );
          if (cartItemIndex !== -1) {
            cartItem = cartData.items[cartItemIndex];
          }
        } else if (effectiveTheme === 'Rebuy' || effectiveTheme === 'rebuy') {
          const classList = Array.from(row.classList);
          const bundleUidClass = classList.find((c) =>
            c.startsWith('property-_bundleuid:'),
          );
          if (bundleUidClass) {
            const bundleUidFromClass = bundleUidClass.split(':')[1];
            cartItemIndex = cartData.items.findIndex(
              (item, idx) =>
                !matchedCartItemIndices.has(idx) &&
                item.properties?.['_bundleUid']?.replace(/-/g, '') ===
                  bundleUidFromClass,
            );
            if (cartItemIndex !== -1) {
              cartItem = cartData.items[cartItemIndex];
            }
          }
          if (!cartItem) {
            const productClass = classList.find(
              (c) => c.startsWith('product-') && !c.startsWith('product-id-'),
            );
            if (productClass) {
              const handle = productClass.replace('product-', '');
              cartItemIndex = cartData.items.findIndex(
                (item, idx) =>
                  !matchedCartItemIndices.has(idx) && item.handle === handle,
              );
              if (cartItemIndex !== -1) {
                cartItem = cartData.items[cartItemIndex];
              }
            }
          }
        } else {
          const idMatch = row.id?.match(/CartItem-(\d+)/);
          if (idMatch) {
            const index = parseInt(idMatch[1]) - 1;
            cartItem = cartData.items[index];
            cartItemIndex = index;
          }
        }

        if (cartItemIndex !== -1) {
          matchedCartItemIndices.add(cartItemIndex);
        }

        return cartItem ? { row, cartItem } : null;
      })
      .filter(Boolean);
  };

  const formatPrice = (cents) => {
    if (
      typeof window !== 'undefined' &&
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (e) {
        debug('Shopify.formatMoney failed, using fallback');
      }
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  const onDocReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();

  const updateElement = (element, props) =>
    element && Object.assign(element, props);
  const setStyles = (element, styles) =>
    element && Object.assign(element.style, styles);
  const queryInRow = (row, selector, selectors) => {
    const effectiveSelectors = selectors || THEME.selectors;
    return row.querySelector(effectiveSelectors[selector]);
  };

  const createBundleSubitem = (item) => {
    const subitem = document.createElement('div');
    subitem.className = 'bundle-subitem';
    subitem.style.cssText =
      'display: flex; align-items: flex-start; margin-bottom: 8px;';

    if (item.image) {
      const img = Object.assign(document.createElement('img'), {
        src: item.image,
        alt: item.product_title,
      });
      img.style.cssText =
        'width: 30px; height: 30px; margin-right: 8px; object-fit: cover; flex-shrink: 0;';
      subitem.appendChild(img);
    }

    const details = document.createElement('div');
    details.className = 'bundle-subitem-details';
    details.style.cssText = 'flex: 1; font-size: 0.875em; line-height: 1.4;';

    const titleDiv = document.createElement('div');
    titleDiv.style.cssText = 'font-weight: 500;';
    titleDiv.textContent = item.product_title;
    details.appendChild(titleDiv);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variantDiv = document.createElement('div');
      variantDiv.style.cssText = 'color: #6b7280; font-size: 0.9em;';
      variantDiv.textContent = item.variant_title;
      details.appendChild(variantDiv);
    }

    const qtyDiv = document.createElement('div');
    qtyDiv.style.cssText = 'color: #6b7280; font-size: 0.9em; margin-top: 2px;';
    qtyDiv.textContent = `Qty: ${item.quantity}`;
    details.appendChild(qtyDiv);
    subitem.appendChild(details);
    return subitem;
  };

  const updateBundlePrices = (bundleRow, items, selectors) => {
    const effectiveSelectors = selectors || THEME.selectors;
    const totalPrice = items.reduce(
      (sum, item) => sum + item.final_line_price,
      0,
    );
    const totalOriginalPrice = items.reduce(
      (sum, item) => sum + item.original_line_price,
      0,
    );

    const unitPriceWrapper = queryInRow(
      bundleRow,
      'unitPriceWrapper',
      effectiveSelectors,
    );
    if (unitPriceWrapper) {
      updateElement(
        unitPriceWrapper.querySelector(
          'span:not(.visually-hidden):not(.compare-at-price)',
        ),
        { textContent: formatPrice(totalPrice) },
      );
      if (totalOriginalPrice > totalPrice) {
        updateElement(
          unitPriceWrapper.querySelector(effectiveSelectors.compareAtPrice),
          { textContent: formatPrice(totalOriginalPrice) },
        );
      }
    }
    updateElement(queryInRow(bundleRow, 'price', effectiveSelectors), {
      textContent: formatPrice(totalPrice),
    });
  };

  const disableBundleQuantityControls = (bundleRow, themeName, selectors) => {
    const effectiveTheme = themeName || THEME.name;
    const effectiveSelectors = selectors || THEME.selectors;
    const qtyElement = queryInRow(bundleRow, 'qtyInput', effectiveSelectors);
    if (effectiveTheme === 'Rebuy' || effectiveTheme === 'rebuy') {
      if (qtyElement) {
        const textNodes = Array.from(qtyElement.childNodes).filter(
          (node) => node.nodeType === Node.TEXT_NODE,
        );
        if (textNodes.length > 0) {
          textNodes[0].textContent = ' 1 ';
        }
      }
    } else {
      updateElement(qtyElement, {
        value: '1',
        readOnly: true,
        disabled: true,
      });
    }
    bundleRow.querySelectorAll(effectiveSelectors.qtyButtons).forEach((btn) =>
      setStyles(btn, {
        visibility: 'hidden',
        opacity: '0',
        pointerEvents: 'none',
      }),
    );
  };

  const setupRemoveButton = (bundleRow, bundleId, selectors, themeName) => {
    const effectiveSelectors = selectors || THEME.selectors;
    const effectiveTheme = themeName || THEME.name;
    const removeButton = queryInRow(
      bundleRow,
      'removeButton',
      effectiveSelectors,
    );
    if (!removeButton) {
      return;
    }

    const wrapper = document.createElement('div');
    const wrapperStyles =
      effectiveTheme === 'Rebuy' || effectiveTheme === 'rebuy'
        ? {
            display: 'inline-block',
            cursor: 'pointer',
            position: 'absolute',
            top: '0',
            right: '0',
            width: '20px',
            height: '20px',
          }
        : { display: 'inline-block', cursor: 'pointer' };
    setStyles(wrapper, wrapperStyles);
    removeButton.parentNode.insertBefore(wrapper, removeButton);
    wrapper.appendChild(removeButton);
    removeButton.href = '#';
    setStyles(removeButton, { pointerEvents: 'none' });

    wrapper.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setStyles(bundleRow, { opacity: '0.6', pointerEvents: 'none' });

      try {
        const updates = {};
        (await (await fetch('/cart.js')).json()).items.forEach((item) => {
          if (item.properties?.['_bundleUid'] === bundleId) {
            updates[item.key] = 0;
          }
        });

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });

        if (response.ok) {
          bundleRow.remove();
          const updatedCartData = await fetchCartData();
          if (updatedCartData) {
            updateCartBubble(updatedCartData);
            lastCartData.page = updatedCartData;
            lastCartData.drawer = updatedCartData;
            lastCartData.rebuy = updatedCartData;
          }
        } else {
          setStyles(bundleRow, { opacity: '', pointerEvents: '' });
        }
      } catch (error) {
        debug('Error removing bundle:', error);
        setStyles(bundleRow, { opacity: '', pointerEvents: '' });
      }
    });
  };

  const updateBundleImage = (bundleRow, product, themeName, selectors) => {
    const effectiveTheme = themeName || THEME.name;
    const effectiveSelectors = selectors || THEME.selectors;
    const imageElement = queryInRow(bundleRow, 'image', effectiveSelectors);
    const mediaContainer = queryInRow(
      bundleRow,
      'imageContainer',
      effectiveSelectors,
    );

    if (product.featured_image) {
      updateElement(imageElement, {
        src: product.featured_image,
        srcset: product.featured_image,
        alt: product.title,
      });
      imageElement?.removeAttribute('data-srcset');
      imageElement?.removeAttribute('data-src');
      imageElement?.removeAttribute('data-widths');
      imageElement?.style.removeProperty('display');
      imageElement?.style.removeProperty('opacity');
      if (mediaContainer && effectiveTheme === 'Horizon') {
        setStyles(mediaContainer, {
          backgroundImage: `url(${product.featured_image})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '',
        });
      }
    } else {
      setStyles(imageElement, { display: 'none' });
      setStyles(mediaContainer, {
        backgroundImage: 'none',
        backgroundColor: '#f3f4f6',
      });
    }
  };

  const addVariantAndSubscription = (
    variantsWrapper,
    variant,
    subscription,
  ) => {
    if (!variantsWrapper || !variant) {
      return;
    }

    const existingPropertyLists = Array.from(
      variantsWrapper.querySelectorAll('ul.list-unstyled'),
    );

    if (variant.title && variant.title !== 'Default Title') {
      const variantDiv = Object.assign(document.createElement('div'), {
        className: 'cart-items__variant',
        textContent: variant.title,
      });
      setStyles(variantDiv, { margin: '5px 0', display: 'block' });
      variantsWrapper.insertBefore(variantDiv, variantsWrapper.firstChild);
    }

    if (
      subscription?.selling_plan &&
      !existingPropertyLists.find((ul) =>
        ul.textContent.includes(subscription.selling_plan.name),
      )
    ) {
      const planList = Object.assign(document.createElement('ul'), {
        className: 'list-unstyled',
      });
      const planItem = document.createElement('li');
      planItem.textContent = subscription.selling_plan.name;
      planList.appendChild(planItem);
      planList.setAttribute('role', 'list');
      (
        existingPropertyLists[existingPropertyLists.length - 1] ||
        variantsWrapper
      ).parentNode.insertBefore(
        planList,
        existingPropertyLists[existingPropertyLists.length - 1]?.nextSibling ||
          null,
      );
    }
  };

  const updateCartBubble = (cartData) => {
    const bundleGroups = groupBundleItems(cartData);
    const bundleItemKeys = new Set(
      Object.values(bundleGroups).flatMap((g) => g.items.map((i) => i.key)),
    );
    const bundleCount = Object.keys(bundleGroups).length;
    const standaloneCount = cartData.items
      .filter((i) => !bundleItemKeys.has(i.key))
      .reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalCount = bundleCount + standaloneCount;

    debug(
      `Updating cart bubble: ${totalCount} visible items (${bundleCount} bundles + ${standaloneCount} standalone)`,
    );

    const bubbleConfig = THEME.cartBubble;
    if (bubbleConfig) {
      const containers = document.querySelectorAll(bubbleConfig.container);
      document.querySelectorAll(bubbleConfig.count).forEach((el) => {
        el.textContent = totalCount;
      });
      containers.forEach((container) => {
        container
          .querySelectorAll(bubbleConfig.visuallyHidden)
          .forEach((el) => {
            el.textContent =
              THEME.name === 'Dawn'
                ? `${totalCount} items`
                : `Total items in cart: ${totalCount}`;
          });
      });
      containers.forEach((el) => {
        el.classList.add('awtomic-ready');
      });
    }
  };

  const createBundleRows = async (
    bundleGroups,
    matches,
    themeName,
    selectors,
  ) => {
    const effectiveTheme = themeName || THEME.name;
    const effectiveSelectors = selectors || THEME.selectors;

    for (const [bundleId, group] of Object.entries(bundleGroups)) {
      const containerSelector =
        effectiveTheme === 'Rebuy' || effectiveTheme === 'rebuy'
          ? 'ul[data-smartcart-items]'
          : effectiveTheme === 'Supply'
            ? '.cart__items'
            : 'tbody';
      const itemSelector =
        effectiveTheme === 'Rebuy' || effectiveTheme === 'rebuy'
          ? `li[data-bundle-id="${bundleId}"]`
          : effectiveTheme === 'Supply'
            ? `.cart__item[data-bundle-id="${bundleId}"]`
            : `[data-bundle-id="${bundleId}"]`;

      if (
        matches[0]?.row.closest(containerSelector)?.querySelector(itemSelector)
      ) {
        continue;
      }

      const bundleProductData =
        group.properties._bundleHandle && group.properties._variantId
          ? await fetchBundleProductData(
              group.properties._bundleHandle,
              group.properties._variantId,
            )
          : null;

      const firstMatch = matches.find(
        (m) => m.cartItem.key === group.items[0].key,
      );
      if (!firstMatch) {
        continue;
      }

      const bundleRow = firstMatch.row.cloneNode(true);
      Object.assign(bundleRow.dataset, {
        bundleId,
        bundleHandle: group.properties._bundleHandle,
        awtomicBundle: 'true',
        key: `bundle-${bundleId}`,
      });

      if (bundleProductData?.product) {
        const { product, variant } = bundleProductData;
        const productUrl = `/products/${product.handle}${variant ? `?variant=${variant.id}` : ''}`;

        updateElement(queryInRow(bundleRow, 'title', effectiveSelectors), {
          textContent: product.title,
          href: productUrl,
        });
        const imageLink = queryInRow(
          bundleRow,
          'imageLink',
          effectiveSelectors,
        );
        if (imageLink) {
          imageLink.href = productUrl;
          imageLink.setAttribute('aria-label', product.title);
        }

        updateBundleImage(
          bundleRow,
          product,
          effectiveTheme,
          effectiveSelectors,
        );

        const variantsWrapper = bundleRow.querySelector(
          effectiveSelectors.variantsWrapper,
        );
        if (variantsWrapper && variant) {
          addVariantAndSubscription(
            variantsWrapper,
            variant,
            group.items[0].selling_plan_allocation,
          );

          const subitemsContainer = Object.assign(
            document.createElement('div'),
            { className: 'bundle-subitems' },
          );
          setStyles(subitemsContainer, { marginTop: '15px' });
          group.items.forEach((item) =>
            subitemsContainer.appendChild(createBundleSubitem(item)),
          );
          variantsWrapper.appendChild(subitemsContainer);
        }
      }

      updateBundlePrices(bundleRow, group.items, effectiveSelectors);
      disableBundleQuantityControls(
        bundleRow,
        effectiveTheme,
        effectiveSelectors,
      );
      setupRemoveButton(
        bundleRow,
        bundleId,
        effectiveSelectors,
        effectiveTheme,
      );

      firstMatch.row.parentNode.insertBefore(bundleRow, firstMatch.row);
      debug(
        `Hiding ${group.items.length} items for bundle ${bundleId}:`,
        group.items.map((i) => i.key),
      );
      group.items.forEach((item) => {
        const match = matches.find((m) => m.cartItem.key === item.key);
        if (match) {
          debug(`  ✓ Hiding item ${item.key}`);
          match.row.style.display = 'none';
          match.row.dataset.awtomicHidden = 'true';
        } else {
          debug(`  ✗ Could not find match for item ${item.key}`);
        }
      });
    }
  };

  const analyzeCart = async () => {
    debug('analyzeCart() called');

    if (isProcessing || Date.now() - lastProcessedTime < 2000) {
      debug('Skipping: already processing or too soon');
      return;
    }

    try {
      const newTheme = detectTheme();
      if (newTheme.name !== THEME.name) {
        debug(`Theme changed from ${THEME.name} to ${newTheme.name}`);
        THEME = newTheme;
      }

      debug('Getting cart context...');
      const contexts = getCartContext();
      debug('Cart contexts:', contexts);

      if (!contexts) {
        debug('No cart contexts found');
        return;
      }

      debug('Fetching cart data...');
      const cartData = await fetchCartData();
      debug(
        'Cart data fetched:',
        cartData ? `${cartData.items.length} items` : 'null',
      );

      if (!cartData) {
        debug('No cart data returned');
        return;
      }

      debug(`Found ${contexts.length} cart context(s) to process`);

      for (const context of contexts) {
        debug(
          `Analyzing ${context.type} cart (${context.theme}) with ${cartData.items.length} items`,
        );

        if (rollupAlreadyDone[context.type]) {
          debug(`Rollup already done for ${context.type} cart, skipping`);
          continue;
        }

        await processCartContext(context, cartData);
      }

      updateCartBubble(cartData);
    } catch (error) {
      console.error('[Awtomic Cart Rollup] Error in analyzeCart:', error);
    }
  };

  const processCartContext = async (context, cartData) => {
    const contextType = context.type;
    if (
      lastCartData[contextType]?.items.length === cartData.items.length &&
      lastCartData[contextType].items.every((item, i) => {
        const newItem = cartData.items[i];
        return (
          newItem &&
          item.key === newItem.key &&
          item.quantity === newItem.quantity
        );
      })
    ) {
      debug(`Cart data unchanged for ${contextType}, skipping`);
      return;
    }

    lastCartData[contextType] = cartData;
    isProcessing = true;
    lastProcessedTime = Date.now();

    let itemsContainer, originalContainer;

    try {
      const contextTheme = context.theme;
      const themeSelectors =
        contextTheme === 'rebuy'
          ? {
              cartItemRow: 'li.rebuy-cart__flyout-item',
              title: '.rebuy-cart__flyout-item-product-title',
              image: '.rebuy-cart__flyout-item-media img',
              imageContainer: '.rebuy-cart__flyout-item-media',
              imageLink: '.rebuy-cart__flyout-item-media a',
              variants: '.rebuy-cart__flyout-item-discount-message',
              variantsWrapper: '.rebuy-cart__flyout-item-info',
              unitPriceWrapper: '.rebuy-cart__flyout-item-price',
              price:
                '.rebuy-money.sale span[tabindex], .rebuy-money span[tabindex]',
              compareAtPrice: '.rebuy-money.compare-at span',
              qtyInput: '.rebuy-cart__flyout-item-quantity-widget-label',
              qtyButtons: '.rebuy-cart__flyout-item-quantity-widget-button',
              removeButton: '.rebuy-cart__flyout-item-remove',
            }
          : THEME.selectors;

      if (contextTheme === 'Rebuy' || contextTheme === 'rebuy') {
        // CONSOLE VERSION: Extra safety for Rebuy
        debug('⚠️  Processing Rebuy cart - using extra safety checks');

        itemsContainer = context.container.querySelector(
          'ul[data-smartcart-items], .rebuy-cart__flyout-items',
        );
        originalContainer = itemsContainer;
        debug(
          `Rebuy container found: ${!!originalContainer}`,
          originalContainer,
        );

        // Safety check: if container is not found or empty, bail out
        if (!originalContainer) {
          debug(
            '⚠️  Rebuy container not found - aborting to prevent breaking Rebuy',
          );
          return;
        }

        // Safety check: Check if Rebuy has already processed items
        const alreadyProcessedItems = originalContainer.querySelectorAll(
          '[data-bundle-id], [data-awtomic-bundle]',
        );
        if (alreadyProcessedItems.length > 0) {
          debug(
            '⚠️  Rebuy items already processed - skipping to prevent conflicts',
          );
          return;
        }
      } else if (contextTheme === 'Supply') {
        if (context.type === 'drawer') {
          // For drawer, look inside the drawer container
          itemsContainer = context.container.querySelector('.cart__items');
          debug(
            'Supply drawer - looking for .cart__items inside drawer container',
          );
          debug('  Drawer container:', context.container);
          debug('  Found .cart__items:', itemsContainer);

          // Safety check: make sure we found the right container with items
          if (itemsContainer) {
            const itemsInContainer =
              itemsContainer.querySelectorAll('.cart__item');
            debug(
              `  Container has ${itemsInContainer.length} .cart__item elements`,
            );
            if (itemsInContainer.length === 0) {
              debug(
                '  ⚠️  Container found but no items - might be wrong container',
              );
            }
          }
        } else {
          // For page, use the same logic as production - find correct [data-products]
          debug(
            'Supply page - looking for [data-products] with .cart__item children',
          );
          const dataProductsContainers =
            document.querySelectorAll('[data-products]');
          debug(
            `  Found ${dataProductsContainers.length} [data-products] elements`,
          );

          for (const container of dataProductsContainers) {
            const itemsInContainer = container.querySelectorAll('.cart__item');
            debug(
              `  [data-products] candidate has ${itemsInContainer.length} .cart__item elements`,
            );
            if (container.querySelector('.cart__item')) {
              itemsContainer = container;
              debug('  ✓ Selected this container (has .cart__item children)');
              break;
            }
          }

          if (!itemsContainer) {
            debug(
              '  ⚠️  No [data-products] with .cart__item found, using fallback',
            );
            itemsContainer = document.querySelector(
              '[data-products], .cart__items',
            );
          }
        }
        originalContainer = itemsContainer;
        debug(
          `Supply container found: ${!!originalContainer}`,
          originalContainer,
        );
      } else {
        const cartTable = (
          context.type === 'drawer' ? context.container : document
        ).querySelector('.cart-items__table, table.cart-items');
        originalContainer = cartTable?.querySelector('tbody');
        itemsContainer = cartTable;
      }

      if (!originalContainer) {
        debug(`No original container found for ${contextTheme} cart`);
        return;
      }

      const bundleGroups = groupBundleItems(cartData);
      const bundleGroupCount = Object.keys(bundleGroups).length;

      if (bundleGroupCount > 0) {
        debug(
          `Found ${bundleGroupCount} bundle group(s):`,
          Object.keys(bundleGroups),
        );

        const bundleItemKeys = new Set(
          Object.values(bundleGroups).flatMap((g) => g.items.map((i) => i.key)),
        );
        const bundleUids = Object.keys(bundleGroups);

        debug(
          `Bundle item keys from cart API (${bundleItemKeys.size}):`,
          Array.from(bundleItemKeys).slice(0, 5),
        );

        const allRows = Array.from(
          originalContainer.querySelectorAll(themeSelectors.cartItemRow),
        );
        debug(
          `Found ${allRows.length} total rows in DOM with selector "${themeSelectors.cartItemRow}"`,
        );
        debug(
          'First 5 DOM row keys:',
          allRows.slice(0, 5).map((r) => r.dataset.key || r.id),
        );

        const visibleBundleItems = allRows.filter((row) => {
          if (contextTheme === 'Rebuy' || contextTheme === 'rebuy') {
            const classList = Array.from(row.classList);
            const bundleUidClass = classList.find((c) =>
              c.startsWith('property-_bundleuid:'),
            );
            if (bundleUidClass) {
              const bundleUidFromClass = bundleUidClass.split(':')[1];
              const normalizedClassUid = bundleUidFromClass.replace(/-/g, '');
              return bundleUids.some(
                (uid) => uid.replace(/-/g, '') === normalizedClassUid,
              );
            }
            return false;
          } else {
            const rowKey = row.dataset.key || row.id;
            const hasKey = bundleItemKeys.has(rowKey);
            const isVisible = row.offsetParent !== null;
            if (!hasKey && rowKey) {
              debug(
                `Row key "${rowKey}" not found in bundleItemKeys (first check)`,
              );
            }
            return hasKey && isVisible;
          }
        });

        debug(`Found ${visibleBundleItems.length} visible bundle items in DOM`);

        if (bundleItemKeys.size > 0 && visibleBundleItems.length === 0) {
          debug(
            `Detected server-side rollup for ${context.type} cart (${bundleItemKeys.size} bundle items hidden in DOM)`,
          );
          rollupAlreadyDone[context.type] = true;
          return;
        }
      } else {
        debug('No bundle groups found in cart');
        return;
      }

      if (contextTheme === 'Rebuy' || contextTheme === 'rebuy') {
        originalContainer
          .querySelectorAll('li.awtomic-processed, li[data-awtomic-hidden]')
          .forEach((item) => item.remove());
      } else if (contextTheme === 'Supply') {
        originalContainer
          .querySelectorAll(
            '.cart__item.awtomic-processed, .cart__item[data-awtomic-hidden]',
          )
          .forEach((item) => item.remove());
      } else {
        itemsContainer
          .querySelectorAll('tbody.awtomic-processed')
          .forEach((clone) => clone.remove());
      }

      let containerToProcess;
      if (
        contextTheme === 'Rebuy' ||
        contextTheme === 'rebuy' ||
        contextTheme === 'Supply'
      ) {
        containerToProcess = originalContainer;
      } else {
        containerToProcess = originalContainer.cloneNode(true);
        containerToProcess.classList.add('awtomic-processed');
        containerToProcess.classList.remove('awtomic-original');
      }

      if (bundleGroupCount > 0) {
        const matches = findAndMatchCartItems(
          cartData,
          containerToProcess,
          themeSelectors,
          contextTheme,
        );
        debug(
          `Matched ${matches.length} items out of ${cartData.items.length} cart items`,
        );
        debug(
          'Matched cart item keys:',
          matches.map((m) => m.cartItem.key),
        );

        // CONSOLE VERSION: Wrap Rebuy operations in try-catch
        if (contextTheme === 'Rebuy' || contextTheme === 'rebuy') {
          try {
            debug('⚠️  Creating bundle rows for Rebuy (with safety wrapper)');
            await createBundleRows(
              bundleGroups,
              matches,
              contextTheme,
              themeSelectors,
            );
            debug('✓ Rebuy bundle rows created successfully');
          } catch (error) {
            console.error(
              '[Awtomic Cart Rollup] Error creating Rebuy bundles (recovered):',
              error,
            );
            debug(
              '⚠️  Rebuy processing failed - cart should still be functional',
            );
            return; // Bail out but don't crash
          }
        } else {
          await createBundleRows(
            bundleGroups,
            matches,
            contextTheme,
            themeSelectors,
          );
        }
      }

      if (
        contextTheme !== 'Rebuy' &&
        contextTheme !== 'rebuy' &&
        contextTheme !== 'Supply'
      ) {
        originalContainer.classList.add('awtomic-original');
        itemsContainer.appendChild(containerToProcess);
      }

      rollupAlreadyDone[contextType] = true;

      debug(`Cart rollup completed for ${context.type} cart (${contextTheme})`);
    } catch (error) {
      console.error(
        `[Awtomic Cart Rollup] Error processing ${context.type} cart:`,
        error,
      );
      // CONSOLE VERSION: Extra logging for debugging
      debug('Error details:', {
        contextType: context.type,
        contextTheme: context.theme,
        itemsContainer,
        originalContainer,
      });
    } finally {
      isProcessing = false;
    }
  };

  // Run immediately without event listeners for console testing
  const init = async () => {
    try {
      debug('Initializing Awtomic Cart Rollup (Console Version)...');
      const cartData = await fetchCartData();
      if (cartData) {
        await Promise.all(
          Object.values(groupBundleItems(cartData))
            .filter(
              (g) => g.properties._bundleHandle && g.properties._variantId,
            )
            .map((g) =>
              fetchBundleProductData(
                g.properties._bundleHandle,
                g.properties._variantId,
              ),
            ),
        );
      }

      window.awtomicCartRollup = {
        analyze: analyzeCart,
        version: '1.0.0-console',
        get theme() {
          return THEME;
        },
        stopPolling: () => {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
            debug('Auto-detection stopped');
          }
        },
      };

      debug('Running initial cart analysis...');
      await analyzeCart();
      debug('Initial analysis complete!');

      // Simple polling for cart visibility (console version only)
      // Note: Browsers may throttle intervals below 4ms
      const pollMs = 25; // Fast polling for near-instant detection
      pollInterval = setInterval(() => {
        const rebuyVisible = !!document.querySelector(
          '#rebuy-cart.is-visible, .rebuy-cart.is-visible, #rebuy-cart[aria-hidden="false"]',
        );
        const supplyDrawerVisible =
          !!document.querySelector('.drawer--is-open');

        // Rebuy cart just opened
        if (rebuyVisible && !lastVisibleCarts.rebuy) {
          debug('🔔 Rebuy cart opened');
          lastVisibleCarts.rebuy = true;
          rollupAlreadyDone.rebuy = false;
          setTimeout(() => analyzeCart(), 10);
        }
        // Rebuy cart just closed
        else if (!rebuyVisible && lastVisibleCarts.rebuy) {
          debug('🔔 Rebuy cart closed');
          lastVisibleCarts.rebuy = false;
        }

        // Supply drawer just opened
        if (supplyDrawerVisible && !lastVisibleCarts.drawer) {
          debug('🔔 Supply drawer opened');
          lastVisibleCarts.drawer = true;
          rollupAlreadyDone.drawer = false;
          setTimeout(() => analyzeCart(), 10);
        }
        // Supply drawer just closed
        else if (!supplyDrawerVisible && lastVisibleCarts.drawer) {
          debug('🔔 Supply drawer closed');
          lastVisibleCarts.drawer = false;
        }
      }, pollMs);

      debug(`✓ Auto-detection enabled (checks every ${pollMs}ms)`);
      debug('You can run window.awtomicCartRollup.analyze() to re-analyze');
      debug(
        'You can run window.awtomicCartRollup.stopPolling() to disable auto-detection',
      );
    } catch (error) {
      console.error(
        '[Awtomic Cart Rollup] Error during initialization:',
        error,
      );
    }
  };

  try {
    init();
  } catch (error) {
    console.error('[Awtomic Cart Rollup] Fatal error:', error);
  }
})();
